/**
 * Renders Strapi rich text format (Slate.js) with proper formatting
 * Handles bold, italic, strikethrough, code, and links
 */

import React from 'react';

export const renderRichText = (content) => {
  if (!content) return null;

  // If it's a string, check if it contains structured sections
  if (typeof content === 'string') {
    if (hasStructuredSections(content)) {
      return parseStructuredText(content);
    }
    return <p className="text-gray-600 leading-relaxed mb-4">{content}</p>;
  }

  // If it's an array of blocks (multiple paragraphs)
  if (Array.isArray(content)) {
    // First, extract all text to check if it has structured sections
    const fullText = extractTextFromBlocks(content);
    
    if (hasStructuredSections(fullText)) {
      return parseStructuredText(fullText);
    }
    
    // If not structured, render blocks normally
    return content.map((block, blockIndex) => renderBlock(block, blockIndex));
  }

  // If it's a single block object
  return renderBlock(content, 0);
};

/**
 * Extract all text content from an array of Slate.js blocks
 */
const extractTextFromBlocks = (blocks) => {
  let text = '';
  
  const extractFromNode = (node, isListItem = false) => {
    if (typeof node === 'string') {
      text += node;
    } else if (node.text) {
      text += node.text;
    } else if (node.children && Array.isArray(node.children)) {
      node.children.forEach((child, idx) => {
        extractFromNode(child, node.type === 'list-item');
        // Add newline after list items
        if (node.type === 'list-item' && idx === node.children.length - 1) {
          text += '\n';
        }
      });
    }
  };
  
  blocks.forEach(block => {
    if (block.type === 'list' && block.children) {
      // For lists, extract each list item separately
      block.children.forEach(listItem => {
        extractFromNode(listItem);
      });
    } else {
      extractFromNode(block);
      text += '\n'; // Add newline between blocks
    }
  });
  
  return text;
};

/**
 * Check if text contains structured section headers
 */
const hasStructuredSections = (text) => {
  const sectionPatterns = ['Product:', 'Applications:', 'Size/Capacity:', 'Capacity:', 'Advantages:', 'Note:', 'Features:'];
  return sectionPatterns.some(pattern => text.includes(pattern));
};

/**
 * Parse and render structured text with section headers and styling
 */
const parseStructuredText = (text) => {
  const sections = [];
  const sectionHeaders = ['Product', 'Applications', 'Size/Capacity', 'Capacity', 'Advantages', 'Note', 'Features'];
  
  // Step 1: Try to split by newlines first
  let lines = text.split('\n').map(line => line.trim()).filter(line => line);
  
  // Step 2: If only 1 line (or 0), split by headers
  if (lines.length <= 1) {
    const headerPattern = new RegExp(`(Product|Applications|Size/Capacity|Capacity|Advantages|Note|Features):\\s*`, 'g');
    const parts = text.split(headerPattern);
    
    lines = [];
    for (let i = 1; i < parts.length; i += 2) {
      const header = parts[i];
      const content = parts[i + 1]?.trim() || '';
      if (header && content) {
        lines.push(`${header}: ${content}`);
      }
    }
  }

  let currentSection = null;
  let currentContent = [];

  for (let line of lines) {
    if (!line) continue;

    // Check if this line starts a new section
    let isNewSection = false;
    let sectionName = null;

    for (let header of sectionHeaders) {
      if (line.startsWith(header + ':')) {
        isNewSection = true;
        sectionName = header;
        
        // Extract content after the header
        const content = line.substring(header.length + 1).trim();
        
        // Save previous section if exists
        if (currentSection && currentContent.length > 0) {
          sections.push({ name: currentSection, content: currentContent });
        }
        
        // Start new section
        currentSection = sectionName;
        currentContent = content ? [content] : [];
        break;
      }
    }

    // If not a new section header, add to current section
    if (!isNewSection && currentSection) {
      currentContent.push(line);
    }
  }

  // Add the last section
  if (currentSection && currentContent.length > 0) {
    sections.push({ name: currentSection, content: currentContent });
  }

  // Render sections
  return (
    <div className="space-y-4">
      {sections.map((section, idx) => (
        <div key={idx} className="mb-6 border-b border-gray-200 pb-4 last:border-b-0">
          <h4 className="font-bold text-catalina-blue mb-3 text-base">{section.name}:</h4>
          {renderSectionContent(section.name, section.content)}
        </div>
      ))}
    </div>
  );
};

/**
 * Render section content based on section type
 */
const renderSectionContent = (sectionName, contentLines) => {
  if (sectionName === 'Advantages' || sectionName === 'Features') {
    // Handle bullet points for Advantages/Features
    const bulletItems = [];
    
    contentLines.forEach(line => {
      // Check for bullet point in this line
      const bullets = line.split(/[•\-]\s*/);
      bullets.forEach((item, idx) => {
        if (idx === 0 && line.startsWith('•')) {
          // First item in split when line starts with bullet
          if (item.trim()) bulletItems.push(item.trim());
        } else if (idx === 0 && line.startsWith('-')) {
          // First item in split when line starts with dash
          if (item.trim()) bulletItems.push(item.trim());
        } else if (idx > 0 && item.trim()) {
          // Subsequent items (these are bullets)
          bulletItems.push(item.trim());
        } else if (!line.startsWith('•') && !line.startsWith('-') && item.trim()) {
          // Regular line without bullet
          bulletItems.push(item.trim());
        }
      });
    });
    
    if (bulletItems.length > 0) {
      return (
        <ul className="list-disc list-inside text-gray-600 space-y-2 pl-2">
          {bulletItems.map((item, idx) => (
            <li key={idx} className="leading-relaxed">{item}</li>
          ))}
        </ul>
      );
    }
  }

  // For other sections, render each line separately with line breaks
  return (
    <div className="text-gray-600 leading-relaxed space-y-2">
      {contentLines.map((line, idx) => (
        <p key={idx} className="m-0">{line}</p>
      ))}
    </div>
  );
};

const renderBlock = (block, blockIndex) => {
  if (!block) return null;

  // Handle different block types
  switch (block.type) {
    case 'paragraph':
      return (
        <p key={blockIndex} className="text-gray-600 leading-relaxed mb-4">
          {renderChildren(block.children)}
        </p>
      );

    case 'heading':
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
      const level = block.level || parseInt(block.type.substring(1)) || 1;
      const headingClasses = {
        1: 'text-3xl font-bold',
        2: 'text-2xl font-bold',
        3: 'text-xl font-semibold',
        4: 'text-lg font-semibold',
        5: 'text-base font-semibold',
        6: 'text-sm font-semibold',
      };
      const headingLevel = Math.min(level, 6);
      return React.createElement(
        `h${headingLevel}`,
        { key: blockIndex, className: `${headingClasses[level]} text-gray-900 mb-4` },
        renderChildren(block.children)
      );

    case 'list':
    case 'bulleted-list':
      return (
        <ul key={blockIndex} className="list-disc list-inside text-gray-600 mb-4 pl-4">
          {block.children?.map((item, idx) => (
            <li key={idx} className="mb-2">
              {renderChildren(item.children)}
            </li>
          ))}
        </ul>
      );

    case 'numbered-list':
      return (
        <ol key={blockIndex} className="list-decimal list-inside text-gray-600 mb-4 pl-4">
          {block.children?.map((item, idx) => (
            <li key={idx} className="mb-2">
              {renderChildren(item.children)}
            </li>
          ))}
        </ol>
      );

    case 'quote':
    case 'blockquote':
      return (
        <blockquote key={blockIndex} className="border-l-4 border-catalina-blue pl-4 italic text-gray-600 my-4">
          {renderChildren(block.children)}
        </blockquote>
      );

    case 'code':
      return (
        <pre key={blockIndex} className="bg-gray-100 p-4 rounded-lg overflow-x-auto mb-4">
          <code className="text-gray-800 font-mono text-sm">
            {renderChildren(block.children)}
          </code>
        </pre>
      );

    default:
      // For any other type, try to render children
      if (block.children) {
        return (
          <div key={blockIndex} className="text-gray-600 mb-4">
            {renderChildren(block.children)}
          </div>
        );
      }
      return null;
  }
};

const renderChildren = (children) => {
  if (!children || !Array.isArray(children)) {
    return children;
  }

  return children.map((child, childIndex) => {
    // If it's a string, return it
    if (typeof child === 'string') {
      return child;
    }

    // If it's an object with text property
    if (child.text !== undefined) {
      return renderTextNode(child, childIndex);
    }

    // If it has children, render them recursively
    if (child.children) {
      return (
        <span key={childIndex}>
          {renderChildren(child.children)}
        </span>
      );
    }

    return null;
  });
};

const renderTextNode = (node, index) => {
  let content = node.text;

  // Handle line breaks (represented as special characters in Strapi)
  content = content.replace(/↵/g, '\n');

  // Build className based on formatting
  let className = '';
  if (node.bold) className += 'font-bold ';
  if (node.italic) className += 'italic ';
  if (node.strikethrough) className += 'line-through ';
  if (node.code) className += 'bg-gray-100 px-2 py-1 rounded font-mono text-sm ';
  if (node.underline) className += 'underline ';

  // If there's no formatting, just return the text
  if (!className) {
    return <span key={index}>{content}</span>;
  }

  // Return with formatting
  return (
    <span key={index} className={className.trim()}>
      {content}
    </span>
  );
};

/**
 * Extract plain text from Strapi rich text format
 * Useful for previews, meta descriptions, etc.
 */
export const extractPlainText = (content, maxLength = null) => {
  if (!content) return '';

  let text = '';

  const extractFromNode = (node) => {
    if (typeof node === 'string') {
      text += node;
    } else if (node.text) {
      text += node.text;
    } else if (node.children && Array.isArray(node.children)) {
      node.children.forEach(extractFromNode);
    }
  };

  if (typeof content === 'string') {
    text = content;
  } else if (Array.isArray(content)) {
    content.forEach(extractFromNode);
  } else if (content.children) {
    extractFromNode(content);
  } else {
    text = JSON.stringify(content);
  }

  // Clean up line break characters
  text = text.replace(/↵/g, ' ').replace(/\n/g, ' ').trim();

  if (maxLength && text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  }

  return text;
};